import { useId } from 'react';

type Motif = 'city' | 'dunes' | 'sea' | 'interior';
const MOTIFS: Motif[] = ['city', 'dunes', 'sea', 'interior'];

function seedOf(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function random(seed: number) {
  let state = seed || 1;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function studioInitials(name: string) {
  const words = name.replace(/['’]s\b/gi, '').split(/\s+/).filter(word => /[\p{L}\p{N}]/u.test(word));
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || 'S').slice(0, 2)).toUpperCase();
}

function StageSet({ motif, rand, sunX, sunY }: { motif: Motif; rand: () => number; sunX: number; sunY: number }) {
  if (motif === 'city') {
    let x = -6;
    const blocks: { x: number; w: number; h: number; lit: number[] }[] = [];
    while (x < 330) {
      const w = 18 + Math.round(rand() * 26);
      const h = 34 + Math.round(rand() * 82);
      blocks.push({ x, w, h, lit: Array.from({ length: 5 }, () => rand()) });
      x += w + 2 + Math.round(rand() * 4);
    }
    return <g className="sc-art-mid">
      {blocks.map((block, index) => <g key={index}>
        <rect x={block.x} y={150 - block.h} width={block.w} height={block.h} className={index % 2 ? 'sc-art-tone-2' : 'sc-art-tone-3'} />
        {block.lit.map((value, row) => value > 0.62 ? <rect key={row} x={block.x + 4 + (row % 2) * (block.w / 2 - 3)} y={150 - block.h + 8 + row * 12} width="4" height="5" className="sc-art-window" /> : null)}
      </g>)}
    </g>;
  }
  if (motif === 'dunes') {
    const crest = (base: number, amp: number) => `M-10 ${base} C 60 ${base - amp * rand()}, 120 ${base + amp * rand()}, 170 ${base - amp * 0.6} S 290 ${base - amp * rand()}, 340 ${base} V 200 H -10 Z`;
    return <g className="sc-art-mid">
      <path d={crest(112, 40)} className="sc-art-tone-1" />
      <path d={crest(128, 34)} className="sc-art-tone-2" />
      <path d={crest(142, 24)} className="sc-art-tone-3" />
    </g>;
  }
  if (motif === 'sea') {
    const horizon = 118;
    return <g className="sc-art-mid">
      <rect x="-10" y={horizon} width="340" height={200 - horizon} className="sc-art-tone-2" />
      {Array.from({ length: 7 }, (_, row) => {
        const y = horizon + 6 + row * 5 + row * row * 0.6;
        const spread = 10 + row * 7;
        return <line key={row} x1={sunX - spread} x2={sunX + spread} y1={y} y2={y} className="sc-art-glint" style={{ opacity: 0.75 - row * 0.09 }} />;
      })}
      {Array.from({ length: 9 }, (_, row) => {
        const y = horizon + 4 + row * 8;
        const start = rand() * 300;
        return <line key={`w${row}`} x1={start} x2={start + 14 + rand() * 30} y1={y} y2={y} className="sc-art-wave" />;
      })}
    </g>;
  }
  const windowX = Math.max(40, Math.min(sunX - 44, 176));
  return <g className="sc-art-mid">
    <path d={`M-10 0 H 330 V 150 H -10 Z M${windowX} ${sunY - 38} v 78 h 104 v -78 Z`} fillRule="evenodd" className="sc-art-wall" />
    <rect x={windowX} y={sunY - 38} width="104" height="78" className="sc-art-pane" />
    <path d={`M${windowX + 52} ${sunY - 38} V ${sunY + 40} M${windowX} ${sunY + 1} H ${windowX + 104}`} className="sc-art-mullion" />
    <rect x={windowX - 4} y={sunY - 42} width="112" height="86" className="sc-art-frame" />
    <rect x="-10" y="138" width="340" height="12" className="sc-art-tone-3" />
    <path d={`M${windowX + 150} 138 v -34 m -10 0 h 20 l -4 -12 h -12 z`} className="sc-art-lamp" />
  </g>;
}

export default function StudioArt({ studioId, name, index, muted = false }: { studioId: string; name: string; index: number; muted?: boolean }) {
  const uid = useId().replace(/:/g, '');
  const rand = random(seedOf(studioId));
  const motif = MOTIFS[Math.floor(rand() * MOTIFS.length)];
  const sunX = 70 + Math.round(rand() * 180);
  const sunY = 52 + Math.round(rand() * 26);
  const standLeft = rand() > 0.5;
  const standX = standLeft ? 34 : 176;
  const beamTarget = standLeft ? 170 : 70;
  const stage = String(index + 1).padStart(2, '0');

  return <svg className={`sc-art${muted ? ' is-muted' : ''}`} viewBox="0 0 320 200" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false" data-motif={motif}>
    <defs>
      <linearGradient id={`${uid}-sky`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#0d0e11" />
        <stop offset="1" stopColor="#17181c" />
      </linearGradient>
      <radialGradient id={`${uid}-glow`}>
        <stop offset="0" stopColor="var(--sa-mag, #e60175)" stopOpacity="0.55" />
        <stop offset="1" stopColor="var(--sa-mag, #e60175)" stopOpacity="0" />
      </radialGradient>
      <linearGradient id={`${uid}-beam`} x1={standLeft ? '0' : '1'} y1="0" x2={standLeft ? '1' : '0'} y2="1">
        <stop offset="0" stopColor="#f1efea" stopOpacity="0.32" />
        <stop offset="1" stopColor="#f1efea" stopOpacity="0" />
      </linearGradient>
      <pattern id={`${uid}-stripes`} width="14" height="12" patternUnits="userSpaceOnUse" patternTransform="skewX(-35)">
        <rect width="7" height="12" fill="#f1efea" />
      </pattern>
    </defs>

    <g className="sc-art-far">
      <rect x="-20" y="-20" width="360" height="240" fill={`url(#${uid}-sky)`} />
      <circle cx={sunX} cy={sunY} r="64" fill={`url(#${uid}-glow)`} className="sc-art-halo" />
      <circle cx={sunX} cy={sunY} r={motif === 'interior' ? 20 : 15} className="sc-art-key" />
    </g>

    <StageSet motif={motif} rand={rand} sunX={sunX} sunY={sunY} />

    <g className="sc-art-near">
      <rect x="-20" y="150" width="360" height="70" className="sc-art-floor" />
      <path d="M-20 150.5 H 340" className="sc-art-mark" />
      <path d={`M${standX} 184 V 104 M${standX} 184 l -12 12 M${standX} 184 l 12 12 M${standX} 184 v 12`} className="sc-art-stand" />
      <g className="sc-art-beam" style={{ transformOrigin: `${standX}px 100px` }}>
        <path d={`M${standX} 100 L ${beamTarget - 46} 200 L ${beamTarget + 46} 200 Z`} fill={`url(#${uid}-beam)`} />
      </g>
      <rect x={standX - 9} y="92" width="18" height="13" rx="2" className="sc-art-head" />
    </g>

    <g className="sc-art-slate" transform="translate(222 124)">
      <rect x="0" y="12" width="84" height="54" rx="3" className="sc-art-slate-board" />
      <text x="8" y="44" className="sc-art-initials">{studioInitials(name)}</text>
      <text x="8" y="58" className="sc-art-slate-label">STAGE {stage}</text>
      <path d="M52 30 h24 M52 40 h24 M52 50 h24" className="sc-art-slate-rule" />
      <g className="sc-art-stick">
        <rect x="0" y="0" width="84" height="11" rx="2" className="sc-art-slate-board" />
        <rect x="0" y="0" width="84" height="11" rx="2" fill={`url(#${uid}-stripes)`} opacity="0.9" />
      </g>
    </g>
  </svg>;
}
